import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, ChefHat, Search, AlertTriangle, Info, TrendingUp, Package, Bike } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { lineCost, validateRecipeLine, unitsCompatible, convertQty, UNIT_HINTS, suggestedPrice } from "@/lib/units";

const UNITS = ["gr", "kg", "ml", "l", "pieza"];

export default function AdminRecipes() {
  const { user } = useAuth();
  const expired = user?.subscription_active === false;
  const [dishes, setDishes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState({}); // dish_id -> recipe
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [draftItems, setDraftItems] = useState([]);          // Insumos (siempre se descuentan)
  const [draftConsumables, setDraftConsumables] = useState([]);          // Solo dine-in
  const [draftDeliveryConsumables, setDraftDeliveryConsumables] = useState([]); // Solo delivery
  const [activeTab, setActiveTab] = useState("items");

  // Active list helpers (so the editor body works with any tab)
  const getActiveList = () => {
    if (activeTab === "consumables") return draftConsumables;
    if (activeTab === "delivery") return draftDeliveryConsumables;
    return draftItems;
  };
  const setActiveList = (next) => {
    if (activeTab === "consumables") return setDraftConsumables(next);
    if (activeTab === "delivery") return setDraftDeliveryConsumables(next);
    return setDraftItems(next);
  };

  const load = async () => {
    try {
      const [dishesRes, ingsRes, recRes] = await Promise.all([
        api.get("/admin/dishes"),
        api.get("/admin/ingredients"),
        api.get("/admin/recipes"),
      ]);
      setDishes(dishesRes.data || []);
      setIngredients(ingsRes.data || []);
      const map = {};
      (recRes.data || []).forEach(r => { map[r.dish_id] = r; });
      setRecipes(map);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (dish) => {
    setEditingDish(dish);
    const existing = recipes[dish.id];
    setDraftItems(existing?.items ? existing.items.map(it => ({ ...it })) : []);
    setDraftConsumables(existing?.consumables ? existing.consumables.map(it => ({ ...it })) : []);
    setDraftDeliveryConsumables(existing?.delivery_consumables ? existing.delivery_consumables.map(it => ({ ...it })) : []);
    setActiveTab("items");
    setOpen(true);
  };

  const addRow = () => {
    setActiveList([...getActiveList(), { ingredient_id: "", quantity: 1, unit: "gr" }]);
  };
  const updateRow = (idx, key, value) => {
    const next = [...getActiveList()];
    next[idx] = { ...next[idx], [key]: key === "quantity" ? Number(value) : value };
    if (key === "ingredient_id") {
      const ing = ingredients.find(i => i.id === value);
      if (ing) next[idx].unit = ing.unit;
    }
    setActiveList(next);
  };
  const removeRow = (idx) => setActiveList(getActiveList().filter((_, i) => i !== idx));

  const save = async () => {
    const cleanList = (list) => list
      .filter(it => it.ingredient_id && Number(it.quantity) > 0)
      .map(it => ({ ingredient_id: it.ingredient_id, quantity: Number(it.quantity), unit: it.unit }));
    const items = cleanList(draftItems);
    const consumables = cleanList(draftConsumables);
    const delivery_consumables = cleanList(draftDeliveryConsumables);
    try {
      await api.put(`/admin/recipes/${editingDish.id}`, {
        dish_id: editingDish.id,
        items,
        consumables,
        delivery_consumables,
      });
      toast.success("Receta guardada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = dishes.filter(d => !search || d.name.toLowerCase().includes(search.toLowerCase()));

  // Apply suggested price to a dish (one-click)
  const [pricingDishId, setPricingDishId] = useState(null);
  const applySuggestedPrice = async (dish, newPrice) => {
    if (!newPrice || newPrice <= 0) return;
    setPricingDishId(dish.id);
    try {
      await api.patch(`/admin/dishes/${dish.id}/price`, { price: newPrice });
      toast.success(`Precio actualizado a ${formatCurrency(newPrice)}`);
      // Update local dishes optimistically
      setDishes(prev => prev.map(x => x.id === dish.id ? { ...x, price: newPrice } : x));
      // Also reflect in the editor if it's open
      if (editingDish?.id === dish.id) setEditingDish({ ...editingDish, price: newPrice });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPricingDishId(null);
    }
  };

  const recipeCost = (dish) => {
    const r = recipes[dish.id];
    if (!r) return 0;
    let total = 0;
    for (const it of r.items || []) {
      const ing = ingredients.find(i => i.id === it.ingredient_id);
      if (!ing) continue;
      const { cost } = lineCost(it, ing);
      total += cost;
    }
    return total;
  };

  return (
    <div className="space-y-6" data-testid="admin-recipes-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1a0f2e]">Recetas</h1>
          <p className="text-[#7248ae] text-sm">Materia prima por platillo · descuenta inventario al cobrar</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7248ae]" />
          <Input
            placeholder="Buscar platillo…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 w-64"
            data-testid="recipe-search-input"
          />
        </div>
      </div>

      {ingredients.length === 0 && (
        <Card className="p-4 bg-[#fcfbfd] border-[#f59e0b]/40 text-sm text-[#92400e]">
          Aún no tienes ingredientes. Crea ingredientes en Inventario antes de configurar recetas.
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recipes-list">
        {filtered.map(d => {
          const r = recipes[d.id];
          const items = r?.items || [];
          const cost = recipeCost(d);
          const price = Number(d.price || 0);
          const margin = price - cost;
          const marginPct = price > 0 ? Math.round((margin / price) * 100) : 0;
          return (
            <Card key={d.id} className="p-4 bg-white border-[#b182ec]/30" data-testid={`recipe-card-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg text-[#1a0f2e] truncate">{d.name}</h3>
                  <p className="text-xs text-[#7248ae]">{d.category || "General"} · {formatCurrency(price)}</p>
                </div>
                {items.length > 0 ? (
                  <Badge className="bg-[#10b981] hover:bg-[#10b981]/80">{items.length} ingredientes</Badge>
                ) : (
                  <Badge className="bg-[#94a3b8] hover:bg-[#94a3b8]/80">Sin receta</Badge>
                )}
              </div>

              {items.length > 0 && (
                <div className="mt-3 space-y-1 text-sm" data-testid={`recipe-items-${d.id}`}>
                  {items.slice(0, 3).map((it, i) => (
                    <div key={i} className="flex justify-between text-[#1a0f2e]">
                      <span className="truncate">{it.ingredient_name || ingredients.find(x => x.id === it.ingredient_id)?.name || "—"}</span>
                      <span className="text-[#7248ae]">{it.quantity} {it.unit}</span>
                    </div>
                  ))}
                  {items.length > 3 && <p className="text-xs text-[#7248ae]">+ {items.length - 3} más…</p>}
                </div>
              )}

              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-[#fcfbfd] py-2">
                  <p className="text-[10px] uppercase text-[#7248ae]">Costo</p>
                  <p className="text-sm font-display text-[#1a0f2e]">{formatCurrency(cost)}</p>
                </div>
                <div className="rounded-lg bg-[#fcfbfd] py-2">
                  <p className="text-[10px] uppercase text-[#7248ae]">Margen</p>
                  <p className="text-sm font-display text-[#1a0f2e]">{formatCurrency(margin)}</p>
                </div>
                <div className="rounded-lg bg-[#fcfbfd] py-2">
                  <p className="text-[10px] uppercase text-[#7248ae]">% Margen</p>
                  <p className={`text-sm font-display ${marginPct >= 60 ? "text-[#10b981]" : marginPct >= 30 ? "text-[#f59e0b]" : "text-[#e11d48]"}`}>{marginPct}%</p>
                </div>
              </div>

              {(() => {
                if (items.length === 0 || cost <= 0) return null;
                const target = marginPct < 30 ? 60 : 65;
                const suggested = suggestedPrice(cost, target);
                if (!suggested || marginPct >= 60 || suggested <= price) return null;
                return (
                  <div
                    className="mt-3 rounded-xl border border-[#7248ae]/30 bg-gradient-to-br from-[#7248ae]/5 to-[#3e166c]/5 p-3"
                    data-testid={`suggest-price-${d.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-[#7248ae]" />
                      <p className="text-xs font-semibold text-[#3e166c]">Sugerencia de precio ideal</p>
                    </div>
                    <p className="text-xs text-[#1a0f2e] leading-snug">
                      Subir a <strong className="text-[#3e166c]">{formatCurrency(suggested)}</strong> alcanza ~{target}% de margen.
                      Hoy estás en {marginPct}% — perdiendo {formatCurrency(suggested - price)} por venta.
                    </p>
                    <Button
                      size="sm"
                      onClick={() => applySuggestedPrice(d, suggested)}
                      disabled={expired || pricingDishId === d.id}
                      className="mt-2 w-full bg-[#7248ae] hover:bg-[#3e166c] text-white"
                      data-testid={`apply-price-${d.id}`}
                    >
                      {pricingDishId === d.id ? "Aplicando…" : `Aplicar ${formatCurrency(suggested)}`}
                    </Button>
                  </div>
                );
              })()}

              <Button
                size="sm"
                className="mt-3 w-full bg-[#3e166c] hover:bg-[#3e166c]/90"
                onClick={() => startEdit(d)}
                disabled={expired || ingredients.length === 0}
                data-testid={`edit-recipe-${d.id}`}
              >
                <ChefHat className="h-4 w-4 mr-2" />
                {items.length === 0 ? "Configurar receta" : "Editar receta"}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Recipe editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="recipe-dialog">
          <DialogHeader>
            <DialogTitle>Receta · {editingDish?.name}</DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2" data-testid="recipe-tabs">
            <TabsList className="grid grid-cols-3 w-full bg-[#fcfbfd]">
              <TabsTrigger value="items" data-testid="recipe-tab-items">
                <ChefHat className="h-3 w-3 mr-1" />Insumos
                {draftItems.length > 0 && <span className="ml-1 text-[10px] text-[#7248ae]">({draftItems.length})</span>}
              </TabsTrigger>
              <TabsTrigger value="consumables" data-testid="recipe-tab-consumables">
                <Package className="h-3 w-3 mr-1" />Consumibles
                {draftConsumables.length > 0 && <span className="ml-1 text-[10px] text-[#7248ae]">({draftConsumables.length})</span>}
              </TabsTrigger>
              <TabsTrigger value="delivery" data-testid="recipe-tab-delivery">
                <Bike className="h-3 w-3 mr-1" />Delivery
                {draftDeliveryConsumables.length > 0 && <span className="ml-1 text-[10px] text-[#7248ae]">({draftDeliveryConsumables.length})</span>}
              </TabsTrigger>
            </TabsList>
            <p className="text-[11px] text-[#7248ae] mt-2 px-1">
              {activeTab === "items" && "Ingredientes principales · se descuentan en TODOS los pedidos (mesa o delivery)."}
              {activeTab === "consumables" && "Vasos, tapas, servilletas, etc. · se descuentan SOLO al cobrar una mesa."}
              {activeTab === "delivery" && "Empaque para llevar · REEMPLAZA a 'Consumibles' cuando el pedido es delivery."}
            </p>
          </Tabs>

          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1 mt-3">
            {(() => {
              const activeList = getActiveList();
              return <>
            {activeList.length === 0 && (
              <p className="text-sm text-[#7248ae] italic">Sin elementos. Agrega los necesarios para una unidad vendida.</p>
            )}
            {activeList.map((it, idx) => {
              const ing = ingredients.find(i => i.id === it.ingredient_id);
              const issues = ing ? validateRecipeLine(it, ing) : [];
              const { cost, compatible } = ing ? lineCost(it, ing) : { cost: 0, compatible: true };
              const converted = ing && compatible && it.unit !== ing.unit
                ? convertQty(it.quantity, it.unit, ing.unit)
                : null;
              return (
                <div key={idx} className="space-y-1 border-b border-[#b182ec]/20 pb-3 last:border-0" data-testid={`recipe-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      {idx === 0 && <Label className="text-xs">Ingrediente</Label>}
                      <Select value={it.ingredient_id} onValueChange={v => updateRow(idx, "ingredient_id", v)}>
                        <SelectTrigger data-testid={`recipe-ing-select-${idx}`}><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                        <SelectContent>
                          {ingredients.map(i => (
                            <SelectItem key={i.id} value={i.id}>{i.name} ({i.unit})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      {idx === 0 && <Label className="text-xs">Cantidad</Label>}
                      <Input type="number" step="0.01" value={it.quantity} onChange={e => updateRow(idx, "quantity", e.target.value)} data-testid={`recipe-qty-input-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Unidad</Label>}
                      <Select value={it.unit} onValueChange={v => updateRow(idx, "unit", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="sm" variant="ghost" onClick={() => removeRow(idx)} className="text-[#e11d48]" data-testid={`recipe-row-remove-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {/* Live conversion preview & cost */}
                  {ing && (
                    <div className="flex items-center justify-between text-xs ml-1 mt-1" data-testid={`recipe-row-meta-${idx}`}>
                      <span className="text-[#7248ae]">
                        {converted != null ? (
                          <span><Info className="h-3 w-3 inline mr-1" />{it.quantity} {it.unit} = {converted.toFixed(2)} {ing.unit} (precio base)</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest">{UNIT_HINTS[it.unit] || ""}</span>
                        )}
                      </span>
                      <span className={`font-display ${compatible ? "text-[#10b981]" : "text-[#e11d48]"}`} data-testid={`recipe-row-cost-${idx}`}>
                        ≈ {formatCurrency(cost)}
                      </span>
                    </div>
                  )}
                  {/* Smart validations */}
                  {issues.map((iss, k) => (
                    <div
                      key={k}
                      className={`flex items-start gap-1.5 text-xs rounded-md px-2 py-1 ${iss.severity === "error" ? "bg-[#e11d48]/10 text-[#9f1239]" : "bg-[#f59e0b]/10 text-[#92400e]"}`}
                      data-testid={`recipe-row-issue-${idx}-${k}`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                      <span>{iss.message}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            <Button size="sm" variant="outline" onClick={addRow} className="mt-2" data-testid="recipe-add-row">
              <Plus className="h-4 w-4 mr-2" />Añadir
            </Button>
            </>;
            })()}
          </div>

          {/* Live totals + save guard */}
          {(() => {
            const totalCost = draftItems.reduce((acc, it) => {
              const ing = ingredients.find(i => i.id === it.ingredient_id);
              return acc + (ing ? lineCost(it, ing).cost : 0);
            }, 0);
            const hasUnitError = draftItems.some(it => {
              const ing = ingredients.find(i => i.id === it.ingredient_id);
              return ing && !unitsCompatible(it.unit, ing.unit);
            });
            const price = Number(editingDish?.price || 0);
            const margin = price - totalCost;
            const marginPct = price > 0 ? (margin / price) * 100 : 0;
            return (
              <div className="mt-2 space-y-2" data-testid="recipe-totals">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg bg-[#fcfbfd] p-3 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-[#7248ae]">Costo total</p>
                    <p className="font-display text-lg text-[#1a0f2e]" data-testid="recipe-total-cost">{formatCurrency(totalCost)}</p>
                  </div>
                  <div className="rounded-lg bg-[#fcfbfd] p-3 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-[#7248ae]">Ganancia</p>
                    <p className="font-display text-lg text-[#1a0f2e]">{formatCurrency(margin)}</p>
                  </div>
                  <div className="rounded-lg bg-[#fcfbfd] p-3 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-[#7248ae]">Margen</p>
                    <p className={`font-display text-lg ${marginPct >= 60 ? "text-[#10b981]" : marginPct >= 30 ? "text-[#f59e0b]" : "text-[#e11d48]"}`}>{marginPct.toFixed(1)}%</p>
                  </div>
                </div>
                {hasUnitError && (
                  <div className="flex items-center gap-2 text-xs rounded-md bg-[#e11d48]/10 text-[#9f1239] px-3 py-2" data-testid="recipe-unit-error">
                    <AlertTriangle className="h-4 w-4" />
                    Hay unidades incompatibles. Corrige antes de guardar.
                  </div>
                )}
                {(() => {
                  if (totalCost <= 0 || !editingDish) return null;
                  const target = marginPct < 30 ? 60 : 65;
                  const suggested = suggestedPrice(totalCost, target);
                  if (!suggested || marginPct >= 60 || suggested <= price) return null;
                  return (
                    <div
                      className="rounded-xl border border-[#7248ae]/30 bg-gradient-to-br from-[#7248ae]/5 to-[#3e166c]/5 p-3"
                      data-testid="recipe-suggest-price-editor"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="h-4 w-4 text-[#7248ae]" />
                        <p className="text-xs font-semibold text-[#3e166c]">Sugerencia de precio</p>
                      </div>
                      <p className="text-xs text-[#1a0f2e] leading-snug">
                        Con este costo, subir a <strong className="text-[#3e166c]">{formatCurrency(suggested)}</strong> alcanza ~{target}% de margen.
                      </p>
                      <Button
                        size="sm"
                        onClick={() => applySuggestedPrice(editingDish, suggested)}
                        disabled={pricingDishId === editingDish.id}
                        className="mt-2 w-full bg-[#7248ae] hover:bg-[#3e166c] text-white"
                        data-testid="recipe-apply-price-editor"
                      >
                        {pricingDishId === editingDish.id ? "Aplicando…" : `Aplicar ${formatCurrency(suggested)}`}
                      </Button>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              onClick={save}
              disabled={[...draftItems, ...draftConsumables, ...draftDeliveryConsumables].some(it => {
                const ing = ingredients.find(i => i.id === it.ingredient_id);
                return ing && !unitsCompatible(it.unit, ing.unit);
              })}
              className="bg-[#3e166c] hover:bg-[#3e166c]/90"
              data-testid="recipe-save-button"
            >
              Guardar receta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
